/**
 * The browser's side of the netcode contract.
 *
 * Everything DOM-free lives here rather than in a component, for the same
 * reason `input-controller.ts` and `settings-io.ts` do: a class that takes
 * plain calls and emits plain callbacks can be driven by a test with no
 * canvas, no socket and no clock, and the parts that must not drift from the
 * server — the timing rules, the message shapes, the numbers — are imported
 * from `@interlock/protocol` rather than restated.
 *
 * What this class does *not* contain is worth stating too:
 *
 * - **No board is ever sent.** `sendInputs` carries actions and a frame
 *   number; `sendHash` carries one 32-bit integer. That is the entire outbound
 *   description of this player's well.
 * - **No guessing.** The incoming-attack meter, the round score and the phase
 *   all come from server messages. `docs/DESIGN.md`, duel: "Incoming meter fed
 *   by server state, not local guessing."
 *
 * ## The socket is not the game
 *
 * A session survives its socket: a heartbeat, a reconnect ladder, and a seat
 * that outlives the connection (`docs/decisions/0041`).
 *
 * - **The heartbeat** is the only way to notice a socket that died without
 *   saying so — a WiFi-to-mobile handoff drops one with no close frame at all.
 *   A `pong` that stops coming back is the evidence.
 * - **The reconnect ladder** backs off 1s, 2s, 4s, 8s and stays there until the
 *   grace window is spent.
 * - **Backgrounding is announced before it happens**, because on iOS there is
 *   no "afterwards" — the page is frozen within seconds and the socket goes
 *   with it.
 *
 * ## The match pauses, so this client stops
 *
 * The *match* stops when any seat goes away (`docs/DESIGN.md`, 2026-08-24).
 * This client owns the seed and could keep simulating through an outage, but
 * playing on would be playing against a stopped board, so it does not.
 *
 * - **`live` is false in every stopped phase.** `paused`, `resuming` and
 *   `frozen` all stop the loop, for the player who went away and the player who
 *   never moved alike.
 * - **A resume is obeyed, not reconciled.** The server's snapshot is the board,
 *   full stop. What that rewinds is the stretch between this socket dying and
 *   the heartbeat noticing it — bounded by `HEARTBEAT_TIMEOUT_MS`, and played
 *   against nobody.
 * - **`resumeAt` restarts the loop, not `room`.** Every peer counts the same
 *   three seconds down on its own clock and starts on the same instant, rather
 *   than waiting to be told a round trip later.
 * - **The curse chips freeze for free.** They are drawn from each driver's own
 *   `scheduledCurses` at that driver's own tick, and a stopped driver's tick
 *   does not move — see `DuelScreen.svelte`.
 */
import { activePieceCells, cloneGameState, createGame } from '@interlock/engine';
import type { ActivePiece, GameState, InputAction, LockResult, Vec2 } from '@interlock/engine';
import {
  C2S,
  COOP_RELAY_SEAT,
  HASH_EVERY_N_PIECES,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_TIMEOUT_MS,
  INPUT_FLUSH_TICKS,
  INPUT_HISTORY_TICKS,
  PROTOCOL_VERSION,
  PRESENCE_BROADCAST_MS,
  S2C,
  defaultDuelRules,
  duelRuleSet,
  duelTickRule,
  graceWindowMs,
  isStoppedPhase,
  reconnectDelayMs,
} from '@interlock/protocol';
import { SimDriver, decodeSnapshot } from '@interlock/sim';
import type {
  DuelRules,
  MatchEndMessage,
  MatchPhase,
  MatchType,
  PongMessage,
  ResumeAtMessage,
  ResumeMessage,
  ResyncMessage,
  RoomMessage,
  RoundEndMessage,
  RoundStartMessage,
  Seat,
  SeatPresence,
  StateMessage,
  SyncMessage,
  WelcomeMessage,
  WireInput,
} from '@interlock/protocol';
import type { CurseEffect, CurseWindow, GarbageSchedule } from '@interlock/sim';

/**
 * The engine options a *classic* duel is played on.
 *
 * The options are a function of the room's rules — hardness scales gravity —
 * and `duelRuleSet` lives in `@interlock/protocol`, which both `apps/client`
 * and `apps/server` depend on, so there is exactly one implementation and
 * nothing to drift.
 */
export const DUEL_ENGINE_OPTIONS = duelRuleSet(defaultDuelRules('duel')).engineOptions;

/**
 * The minimum of a Colyseus room this client needs.
 *
 * Narrow on purpose: a `Room` is a large object with a schema serializer, a
 * state proxy and a reconnection token on it, and this class uses four of its
 * methods. Depending on the four means `test/net/duel-client.test.ts` can hand
 * it a fake in ten lines, and means nothing here quietly starts depending on
 * Colyseus state synchronisation — which the server does not use.
 */
export interface RoomLike {
  send(type: string, payload?: unknown): void;
  onMessage<T = unknown>(type: string, handler: (message: T) => void): unknown;
  onLeave(handler: (code: number) => void): unknown;
  leave(consented?: boolean): Promise<number>;
}

/**
 * `reconnecting`: the server has not heard from us and a grace window is
 * running.
 *
 * It is a stopped game. The match stops for everybody when a seat goes away,
 * so a client in this state has stopped too, and what it shows is the pause
 * overlay and the countdown rather than a board it is playing alone.
 */
export type ConnectionStatus =
  'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'refused';

export interface DuelView {
  status: ConnectionStatus;
  statusDetail: string;
  code: string;
  seat: Seat | null;
  role: 'player' | 'spectator';
  phase: MatchPhase;
  room: RoomMessage | null;
  state: StateMessage | null;
  round: number;
  roundEnd: RoundEndMessage | null;
  matchEnd: MatchEndMessage | null;
  /** True from the moment a round is announced until its first tick. */
  countingDown: boolean;
  countdownMs: number;
  resyncs: number;
  /** How many times this client has rejoined and been handed its seat back. */
  resumes: number;
  /**
   * Milliseconds left before this client's own seat stops being held. Counted
   * down locally, because while the socket is gone nothing is arriving to
   * count it down *for* us — the server's own number takes over the moment one
   * does.
   */
  graceMsLeft: number | null;
  /** Which rung of the backoff ladder the next attempt is on, from 1. */
  reconnectAttempt: number;
  /** This seat's presence and the opponent's, as the server broadcasts them. */
  presence: SeatPresence;
  opponentPresence: SeatPresence;
  opponentGraceMsLeft: number | null;
  /**
   * How much consented pause each seat has left in this round
   * (`PAUSE_BUDGET_MS`), straight from the server's room view. Both, for the
   * same reason both grace countdowns are here: the player waiting has as much
   * right to know how long the wait can last as the player spending it.
   */
  pauseMsLeft: number | null;
  opponentPauseMsLeft: number | null;
  matchType: MatchType;
  /**
   * True while the whole match has stopped — a seat is away, the grace window
   * has expired on a friendly match, or the resume countdown is running. The
   * one flag the pause overlay is shown on.
   */
  matchStopped: boolean;
  /**
   * Milliseconds until play resumes, or `null` when no countdown is running.
   * Counted down locally from the server's `resumeAt`, so the 3-2-1 is as
   * synchronised across the room as the network allows.
   */
  resumeInMs: number | null;
  /** The match tick play will resume on, as the server announced it. */
  resumeAtTick: number | null;
  /**
   * Which duel this room is playing, and how it is configured — the thing the
   * lobby and the duel HUD describe. It arrives with the first `room` view and
   * again with every `roundStart` and `resume`, and it is what this client
   * builds its boards from, so the screen and the simulation can never be
   * describing two different games.
   */
  rules: DuelRules;
  /**
   * Each seat's gravity handicap this round, in thousandths — `1000` is
   * neutral. Straight from
   * the last `roundStart` or `resume`, and what this client's own driver and
   * its mirror of the opponent are each built with. See `docs/decisions/0033`.
   */
  handicap: readonly [number, number];
  /**
   * Milliseconds left on a `duelSprint` time limit, straight from the server's
   * `state` broadcast, or `null` when the variant has no clock.
   */
  timeLeftMs: number | null;
  /**
   * `tug`'s rope, straight from the server's `state` broadcast, `null` for
   * every other variant. `docs/DESIGN.md`: "Rope position lives in server
   * state, mirrored to clients (like the incoming meter)."
   */
  rope: number | null;
}

export interface DuelClientCallbacks {
  /** Called whenever anything the UI shows has changed. */
  readonly onView: (view: DuelView) => void;
  /** Called when a round's boards are (re)created, so the screen can attach renderers. */
  readonly onBoards: (own: GameState | null, opponent: GameState | null) => void;
  /**
   * Called when reconciliation moved this player's own piece by more than one
   * cell, with the cells it ended up on.
   *
   * `docs/prompts/06-coop.md`: "Reconciliation never teleports a piece more
   * than one cell without a visible correction cue." One cell is the ordinary
   * rounding of a prediction and is not worth a flash; anything larger is the
   * player's piece appearing somewhere they did not put it, and they are owed
   * an explanation for it. Co-op only — a duel client's own board is never
   * rewound except by a resync, which the screen already reports.
   */
  readonly onCorrection?: (cells: readonly Vec2[]) => void;
  /**
   * Called after `tick()` steps this player's own board, with every lock
   * that happened this tick (usually zero or one). `apps/client/src/audio`
   * and the particle burst both need `LockResult`'s `lines`/`kind`/`combo`/
   * `backToBack` — data `DuelScreen.svelte`'s own line-count comparison
   * cannot recover after the fact. Own board only: the opponent's is relayed
   * and synced, not stepped locally, so there is no local `LockResult` for
   * it to report.
   */
  readonly onLocks?: (locks: readonly LockResult[]) => void;
  /**
   * Open a fresh socket to the same room, presenting whatever identity is
   * needed to be given the same seat back.
   *
   * Its presence is what turns resilience on: with no way back, a heartbeat
   * that notices a dead socket has nothing to do with the news, and a client
   * that kept simulating into a void would only be lying to its player. Absent
   * — which is what every test that is not about reconnection passes — a
   * dropped socket ends the session.
   */
  readonly reconnect?: () => Promise<RoomLike>;
}

/**
 * A view with nothing in it yet.
 *
 * Exported because `DuelScreen.svelte` needs exactly this to seed its own
 * `$state` before a socket has said anything, and a screen writing out
 * nineteen default fields by hand is nineteen chances to drift from the
 * defaults this file actually uses.
 */
export function emptyDuelView(): DuelView {
  return {
    status: 'idle',
    statusDetail: '',
    code: '',
    seat: null,
    role: 'player',
    phase: 'lobby',
    room: null,
    state: null,
    round: 0,
    roundEnd: null,
    matchEnd: null,
    countingDown: false,
    countdownMs: 0,
    resyncs: 0,
    resumes: 0,
    graceMsLeft: null,
    reconnectAttempt: 0,
    presence: 'online',
    opponentPresence: 'online',
    opponentGraceMsLeft: null,
    pauseMsLeft: null,
    opponentPauseMsLeft: null,
    matchType: 'friendly',
    matchStopped: false,
    resumeInMs: null,
    resumeAtTick: null,
    rules: defaultDuelRules(),
    handicap: [1000, 1000],
    timeLeftMs: null,
    rope: null,
  };
}

export class DuelClient {
  private room: RoomLike | null = null;
  private view: DuelView = emptyDuelView();
  private sessionToken = '';

  /**
   * The board on screen. `null` outside a round.
   *
   * In a duel this is *the* board: this client owns the seed, so what it
   * simulates is authoritative until the server says otherwise. In co-op it is
   * a **prediction** — `confirmed` is the real one and this is that board
   * fast-forwarded through the local inputs the server has not echoed back yet.
   * The screen renders this one either way.
   */
  own: SimDriver | null = null;
  /** The opponent's board, simulated locally from the server's relayed inputs. `null` in co-op. */
  opponent: SimDriver | null = null;
  /**
   * The shared co-op board as the server's own relay describes it. `null`
   * outside a co-op round.
   *
   * Two players act on one well, so this client cannot be the authority for it
   * the way it is for its own duel board — the other player's inputs are not
   * knowable in advance, and the piece they collide with is not this client's
   * to move. `docs/prompts/06-coop.md`: "The server simulates; the clients
   * predict and reconcile." This is the half that is not predicted: every event
   * in it is one the server actually consumed, so it is bit-identical to the
   * server's copy and it is what the hash checkpoints are taken from.
   */
  confirmed: SimDriver | null = null;
  /** True while this room is playing one of the co-op variants. */
  private coop = false;

  private frame = 0;
  private pending: WireInput[] = [];
  /**
   * Every input of the current run of the round, kept for
   * `INPUT_HISTORY_TICKS`.
   *
   * `pending` is what has not been flushed *yet*; this is what has been played
   * at all. They are different because a rejoin cannot trust the flush
   * boundary: the last batch before a socket died may or may not have arrived,
   * and only the server knows — it says so as `fromTick`, and everything from
   * there forward is re-sent from here.
   */
  private history: WireInput[] = [];
  /**
   * Which run of the round this client is on: 0 until a resync or a resume,
   * then whatever it said. Stamped on every input and hash so the server can
   * ignore the ones that were already in flight when it decided to rewind us.
   */
  private epoch = 0;
  private reportedTopOut = false;
  private startTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private graceTimer: ReturnType<typeof setInterval> | null = null;
  /** The 3-2-1 after a pause: one interval that paints it, one timeout that ends it. */
  private resumeTickTimer: ReturnType<typeof setInterval> | null = null;
  private resumeTimer: ReturnType<typeof setTimeout> | null = null;
  private resumeEndsAt = 0;
  private roundStartsAt = 0;
  private live = false;
  /**
   * True while the *match* has stopped and this client must not step its board.
   *
   * Separate from `live`, which means "this round has begun", because the two
   * are decided by different things and can disagree. The sharpest case: a seat
   * goes away during a round's own three-second countdown. `live` is still
   * false and the timer that will set it is already running — so a gate made
   * only of `live` would let that timer fire mid-pause and start a board the
   * rest of the room has stopped. This flag is what a stopped match sets, and
   * only the resume countdown reaching zero (or a fresh round) clears it.
   */
  private stopped = false;
  private paused = false;
  private leaving = false;
  private lastPongMs = 0;
  private attempt = 0;
  private graceEndsAt = 0;
  /**
   * True from the moment a rejoin is asked for until it settles.
   *
   * `tryReconnect` has three callers — the ladder's own timer, `setPaused`
   * when the page comes back, and the screen's "try again" button — and
   * nothing stops two of them landing inside the same round trip. Two rejoins
   * in flight means two sockets for one seat: whichever resolves second is
   * attached over the first, which is left open and unowned.
   */
  private reconnectInFlight = false;

  constructor(private readonly callbacks: DuelClientCallbacks) {}

  get snapshot(): DuelView {
    return { ...this.view };
  }

  get token(): string {
    return this.sessionToken;
  }

  /** True while this client's own board should be ticking. */
  get isPlaying(): boolean {
    return this.live && !this.paused && !this.stopped && this.own !== null;
  }

  /** True while the socket is gone and this client is simulating on its own. */
  get isReconnecting(): boolean {
    return this.view.status === 'reconnecting';
  }

  /** The join options every connection sends. The version gate reads these. */
  static joinOptions(
    name: string,
    role: 'player' | 'spectator',
    sessionToken?: string,
    /**
     * A socket ticket from `POST /api/auth/ticket`, when the player is signed
     * in. Absent for a guest, for an offline install and for a server with no
     * accounts — which is why it is the last, optional argument rather than
     * something the join is restructured around. See
     * `@interlock/protocol`'s `JoinOptions.accountToken`.
     */
    accountToken?: string,
  ): {
    protocolVersion: number;
    name: string;
    role: 'player' | 'spectator';
    sessionToken?: string;
    accountToken?: string;
  } {
    return {
      protocolVersion: PROTOCOL_VERSION,
      name,
      role,
      ...(sessionToken === undefined || sessionToken === '' ? {} : { sessionToken }),
      ...(accountToken === undefined || accountToken === '' ? {} : { accountToken }),
    };
  }

  /**
   * Take over a socket. Called once on entering the room, and again on every
   * successful rejoin — a reconnection is a *new* room object, so the handlers
   * are registered again against it rather than migrated.
   */
  attach(room: RoomLike, role: 'player' | 'spectator'): void {
    this.room = room;
    this.leaving = false;
    this.patch({ status: 'connected', role, statusDetail: '' });

    room.onMessage(S2C.welcome, (message: WelcomeMessage) => {
      this.sessionToken = message.sessionToken;
      this.patch({ code: message.code, seat: message.seat, role: message.role });
    });
    room.onMessage(S2C.room, (message: RoomMessage) => {
      this.applyRoom(message);
    });
    room.onMessage(S2C.state, (message: StateMessage) => {
      this.patch({ state: message, timeLeftMs: message.timeLeftMs, rope: message.rope });
    });
    room.onMessage(S2C.roundStart, (message: RoundStartMessage) => {
      this.beginRound(message);
    });
    room.onMessage(S2C.sync, (message: SyncMessage) => {
      this.applySync(message);
    });
    room.onMessage(S2C.resync, (message: ResyncMessage) => {
      this.applyResync(message);
    });
    room.onMessage(S2C.resume, (message: ResumeMessage) => {
      this.applyResume(message);
    });
    room.onMessage(S2C.resumeAt, (message: ResumeAtMessage) => {
      this.applyResumeAt(message);
    });
    room.onMessage(S2C.roundEnd, (message: RoundEndMessage) => {
      this.live = false;
      this.patch({ roundEnd: message });
    });
    room.onMessage(S2C.matchEnd, (message: MatchEndMessage) => {
      this.live = false;
      // An ending is not a stop: there is nothing left to resume into, and the
      // results overlay is what the screen shows now.
      this.stopped = false;
      this.stopReconnecting();
      this.stopResumeCountdown();
      this.patch({
        matchEnd: message,
        phase: 'matchOver',
        graceMsLeft: null,
        matchStopped: false,
        resumeInMs: null,
        resumeAtTick: null,
      });
    });
    room.onMessage(S2C.pong, (_message: PongMessage) => {
      this.lastPongMs = Date.now();
    });
    room.onMessage(S2C.refused, (message: { reason: string; message: string }) => {
      this.stopReconnecting();
      this.patch({ status: 'refused', statusDetail: message.message });
    });
    room.onLeave(() => {
      this.onSocketClosed();
    });

    this.startHeartbeat();
  }

  setReady(ready: boolean): void {
    this.room?.send(C2S.ready, { ready });
  }

  /** `docs/DESIGN.md`, curses: "players pick three before the match." */
  setLoadout(effects: readonly CurseEffect[]): void {
    this.room?.send(C2S.loadout, { effects });
  }

  requestRematch(): void {
    this.room?.send(C2S.rematch, {});
  }

  /**
   * The page went to the background, or came back.
   *
   * `docs/DESIGN.md`, PWA requirements: "Pause on `visibilitychange`, and tell
   * the server before the socket dies." The order matters more than the
   * message does: iOS freezes a hidden page within seconds and takes the socket
   * with it, so this has to go out on the `visibilitychange` event itself.
   * Afterwards there is no code running to send it, and what the server would
   * see instead is a player who vanished mid-round.
   */
  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    this.room?.send(C2S.pause, { paused });
    this.patch({ presence: paused ? 'paused' : 'online' });
    if (paused) return;
    // Coming back is also the moment to find out whether the socket survived
    // the trip. If it did not, the ladder is already running; if it has not
    // noticed yet, try immediately rather than waiting out a heartbeat.
    this.lastPongMs = Date.now();
    if (this.room === null && this.callbacks.reconnect !== undefined) this.tryReconnect();
  }

  async leave(): Promise<void> {
    this.leaving = true;
    this.stopTimers();
    this.stopHeartbeat();
    this.stopReconnecting();
    this.live = false;
    const room = this.room;
    this.room = null;
    // Consented: this is a departure, not an interruption, and the server must
    // not hold a seat for someone who pressed Leave.
    if (room !== null) await room.leave(true);
  }

  /**
   * Advance this client's own board by one tick and post the result.
   *
   * Called once per engine tick by the duel screen's own fixed-timestep loop
   * (`screens/DuelScreen.svelte`): the same accumulator as the solo
   * `GameLoop`, but a loop of its own, because what it drives is this method
   * rather than the engine's `step`.
   */
  tick(actions: readonly InputAction[]): void {
    const driver = this.own;
    // `live` is the whole gate, and it is false in every stopped phase. The
    // client has the seed and could keep playing through an outage, but the
    // *match* stops (`docs/DESIGN.md`, 2026-08-24): a board that carried on
    // would be playing against nobody, and every tick of it would be rewound
    // by the resume.
    if (driver === null || !this.live || this.paused || this.stopped) return;

    // A *predicted* top-out is not a top-out. It is this client's guess about a
    // board it does not own, made without the partner's inputs, and it is
    // discarded and rebuilt on the next relay. Stopping on it would stop
    // reporting frames, which would stall the shared board for everyone — so
    // in co-op the question is only ever asked of `confirmed`, which is the
    // server's own board.
    const dead = this.coop ? (this.confirmed?.toppedOut ?? false) : driver.toppedOut;
    if (dead) {
      // The last thing a dying board does is tell the server where it died.
      // Without it the server's replay stops short of the top-out and the
      // round never ends.
      if (!this.reportedTopOut) {
        this.reportedTopOut = true;
        this.flushInputs();
      }
      return;
    }

    // In a shared well every event carries the slot it belongs to, because the
    // board it is applied to has two pieces on it. The server stamps its own
    // copy the same way from the seat the socket is sitting in; this one is for
    // the local prediction, which has nobody to ask.
    const slot = this.coop ? (this.view.seat ?? 0) : undefined;
    const events: WireInput[] = actions.map((action) =>
      slot === undefined ? { frame: this.frame, action } : { frame: this.frame, action, slot },
    );
    const result = driver.stepOnce(events);
    if (result.locks.length > 0) this.callbacks.onLocks?.(result.locks);
    this.pending.push(...events);
    this.history.push(...events);
    this.frame = driver.tick;

    if (this.frame % INPUT_FLUSH_TICKS === 0) this.flushInputs();

    // A prediction's hash is not evidence of anything: it is a board built from
    // half the inputs, on purpose. The co-op checkpoints that get sent are
    // `confirmed`'s, taken in `applyCoopSync`.
    if (this.coop) {
      driver.takeCheckpoints();
      return;
    }

    for (const checkpoint of driver.takeCheckpoints()) {
      // Offline, a checkpoint is dropped rather than buffered: the rejoin
      // replays these same pieces from the server's own snapshot and produces
      // the same checkpoints again, and *those* are the ones worth sending —
      // they are consistent with the board the server will actually have.
      this.room?.send(C2S.hash, {
        piecesPlaced: checkpoint.piecesPlaced,
        hash: checkpoint.hash,
        epoch: this.epoch,
      });
    }
  }

  private flushInputs(): void {
    const room = this.room;
    this.trimHistory();
    if (room === null) {
      // Nothing to flush into. The events are not lost — they are in
      // `history`, which is what a rejoin re-sends.
      this.pending = [];
      return;
    }
    room.send(C2S.input, { frame: this.frame, events: this.pending, epoch: this.epoch });
    this.pending = [];
  }

  /** Keep the last `INPUT_HISTORY_TICKS` of play and forget the rest. */
  private trimHistory(): void {
    const oldest = this.frame - INPUT_HISTORY_TICKS;
    if (oldest <= 0) return;
    const keepFrom = this.history.findIndex((event) => event.frame >= oldest);
    if (keepFrom > 0) this.history = this.history.slice(keepFrom);
  }

  private beginRound(message: RoundStartMessage): void {
    this.stopTimers();
    this.live = false;
    // A new round is a running match by definition: whatever stopped the last
    // one is over, and the boards are about to be rebuilt anyway.
    this.stopped = false;
    // The rules travel with the round, exactly as the seed does — a board is
    // built from what this message says, never from something remembered.
    const rules = message.rules;
    const handicap = message.handicap;
    this.coop = duelRuleSet(rules).coop;
    const mySeat = this.view.seat ?? 0;
    const theirSeat = mySeat === 0 ? 1 : 0;
    this.own = this.newDriver(message.seed, rules, handicap[mySeat]);
    if (this.coop) {
      // One well. `confirmed` and `own` start as two copies of the same deal
      // and part company only by however far this client has predicted ahead.
      this.confirmed = this.newDriver(message.seed, rules);
      this.opponent = null;
    } else {
      this.confirmed = null;
      // Both seats play the identical variant *and* the identical seed. In
      // `duelSprint` that is the mode itself (`docs/DESIGN.md`: "both players
      // race the same piece sequence"); in the other two it is what stops
      // either player claiming the bag favoured the other. Each board still
      // gets *its own seat's* handicap, which is why this is not the same
      // call twice.
      this.opponent = this.newDriver(message.seed, rules, handicap[theirSeat]);
    }
    this.frame = 0;
    this.pending = [];
    this.history = [];
    this.epoch = 0;
    this.reportedTopOut = false;
    this.roundStartsAt = Date.now() + message.startsInMs;

    this.callbacks.onBoards(this.own.state, this.opponent?.state ?? null);
    this.patch({
      round: message.round,
      rules,
      handicap,
      roundEnd: null,
      matchEnd: null,
      countingDown: true,
      countdownMs: message.startsInMs,
      matchStopped: false,
      resumeInMs: null,
      resumeAtTick: null,
    });

    // `startsInMs` is not decoration: the server announces a round at the
    // start of its countdown and only begins replaying at the end of it. A
    // client that started simulating on the announcement would be a whole
    // countdown ahead of the authority for the rest of the round.
    this.countdownTimer = setInterval(() => {
      const left = Math.max(0, this.roundStartsAt - Date.now());
      this.patch({ countdownMs: left });
    }, 100);

    this.startTimer = setTimeout(
      () => {
        this.live = true;
        this.stopTimers();
        this.patch({ countingDown: false, countdownMs: 0 });
      },
      Math.max(0, message.startsInMs),
    );
  }

  /**
   * The one place this client constructs a simulation.
   *
   * Both halves of a variant — the engine options and the per-tick rule — come
   * out of the same `duelRuleSet`/`duelTickRule` pair the server calls, from
   * the same `DuelRules` bytes the server sent. That is the whole mechanism by
   * which a rising floor rises on the same tick in three places at once.
   */
  private newDriver(seed: number, rules: DuelRules, gravityPermille = 1000): SimDriver {
    const tickRule = duelTickRule(rules);
    return new SimDriver(createGame({ seed, ...duelRuleSet(rules).engineOptions }), {
      hashEveryNPieces: HASH_EVERY_N_PIECES,
      ...(tickRule === null ? {} : { tickRule }),
      gravityPermille,
    });
  }

  /** A driver over a snapshot the server sent — a resync's board, or a resume's. */
  private driverFromSnapshot(
    state: GameState,
    rules: DuelRules,
    scheduled: readonly GarbageSchedule[],
    gravityPermille = 1000,
    curses?: readonly CurseWindow[],
  ): SimDriver {
    const tickRule = duelTickRule(rules);
    const driver = new SimDriver(state, {
      hashEveryNPieces: HASH_EVERY_N_PIECES,
      ...(tickRule === null ? {} : { tickRule }),
      gravityPermille,
    });
    driver.scheduleGarbageBatches(scheduled);
    driver.scheduleCurses(curses);
    return driver;
  }

  /**
   * Apply the server's relayed stream.
   *
   * For this client's own seat only the garbage matters — the events are its
   * own, already applied. For the other seat both matter, and feeding them
   * through the same `SimDriver` is what makes the opponent's board on screen
   * the server's own board rather than a picture of it
   * (`docs/decisions/0013`).
   */
  private applySync(message: SyncMessage): void {
    if (this.coop) {
      this.applyCoopSync(message);
      return;
    }
    if (this.view.seat !== null && message.seat === this.view.seat) {
      this.own?.scheduleGarbageBatches(message.garbage);
      this.own?.scheduleCurses(message.curses);
      return;
    }
    const opponent = this.opponent;
    if (opponent === null) return;
    opponent.enqueueInputs(message.events);
    opponent.scheduleGarbageBatches(message.garbage);
    opponent.scheduleCurses(message.curses);
    opponent.advanceTo(message.toTick);
  }

  /**
   * The shared well's authoritative stream, and the reconciliation it forces.
   *
   * One relay describes the whole board — both players' inputs, slot-stamped,
   * plus any garbage — so it is applied to `confirmed` and nothing else.
   * Everything downstream falls out of that:
   *
   * - **The hashes come from here.** `confirmed` is built from exactly what the
   *   server consumed, so a mismatch means a relay went missing, which is worth
   *   knowing; a mismatch on the *prediction* would only mean the prediction
   *   was a prediction.
   * - **The prediction is rebuilt, not patched.** Once `confirmed` has moved,
   *   the board on screen is redrawn as "the server's board, plus the local
   *   inputs it has not echoed back yet". There is no way for the two to drift,
   *   because the predicted one is discarded every time.
   */
  private applyCoopSync(message: SyncMessage): void {
    const confirmed = this.confirmed;
    if (confirmed === null || message.seat !== COOP_RELAY_SEAT) return;

    confirmed.enqueueInputs(message.events);
    confirmed.scheduleGarbageBatches(message.garbage);
    confirmed.advanceTo(message.toTick);

    for (const checkpoint of confirmed.takeCheckpoints()) {
      this.room?.send(C2S.hash, {
        piecesPlaced: checkpoint.piecesPlaced,
        hash: checkpoint.hash,
        epoch: this.epoch,
      });
    }

    this.rebuildPrediction();
  }

  /**
   * Redraw the predicted board from the confirmed one plus whatever this
   * player has done since.
   *
   * Cheap by construction: `confirmed` is at most one relay interval behind, so
   * the re-simulation is a handful of ticks over a cloned state. The alternative
   * — trusting the predicted board and folding the partner's inputs into it
   * late — would apply two players' moves in an order neither the server nor
   * the other client used, which is precisely the divergence the whole hash
   * mechanism exists to catch.
   */
  private rebuildPrediction(): void {
    const confirmed = this.confirmed;
    if (confirmed === null) return;
    const slot = this.view.seat ?? 0;
    const before = pieceAt(this.own?.state ?? null, slot);

    // Anything the server has already consumed is in the base now. Re-applying
    // it would play this player's last half-second twice.
    this.history = this.history.filter((event) => event.frame >= confirmed.tick);
    this.pending = this.pending.filter((event) => event.frame >= confirmed.tick);

    const driver = this.driverFromSnapshot(
      cloneGameState(confirmed.state),
      this.view.rules,
      confirmed.scheduledGarbage,
    );
    driver.enqueueInputs(this.history);
    driver.advanceTo(Math.max(confirmed.tick, this.frame));
    // A prediction is not evidence; its checkpoints are dropped unread.
    driver.takeCheckpoints();

    this.own = driver;
    this.frame = driver.tick;

    const after = pieceAt(driver.state, slot);
    if (teleported(before, after))
      this.callbacks.onCorrection?.(activePieceCells(driver.state, slot));
    this.callbacks.onBoards(driver.state, null);
  }

  private applyResync(message: ResyncMessage): void {
    if (this.view.seat === null || message.seat !== this.view.seat) return;
    const state = decodeSnapshot(message.snapshot);
    if (this.coop) {
      // The repair replaces the *confirmed* board, and the prediction is then
      // rebuilt from it as it is after any relay. Nothing local survives: the
      // server has just said this client's copy of the shared well was wrong,
      // and the inputs it was still holding belong to that wrong copy.
      this.confirmed = this.driverFromSnapshot(state, this.view.rules, message.scheduled);
      this.confirmed.takeCheckpoints();
      this.pending = [];
      this.history = [];
      this.frame = state.tick;
      this.epoch = message.epoch;
      this.reportedTopOut = false;
      this.rebuildPrediction();
      this.patch({ resyncs: this.view.resyncs + 1 });
      return;
    }
    // The room's rules — and this seat's own handicap — not the message's: a
    // resync repairs a board inside a round whose variant and whose handicap
    // have not changed and cannot.
    const driver = this.driverFromSnapshot(
      state,
      this.view.rules,
      message.scheduled as readonly GarbageSchedule[],
      this.view.handicap[this.view.seat],
      message.curses,
    );
    this.own = driver;
    this.frame = state.tick;
    // Everything queued for a frame past the snapshot belonged to a history
    // the server has just replaced. The server drops what it was holding at
    // the same moment; the epoch is what fences off the messages already in
    // flight the other way, which the server would otherwise accept and
    // replay against a board that no longer exists.
    this.pending = [];
    this.history = [];
    this.epoch = message.epoch;
    this.reportedTopOut = false;
    this.callbacks.onBoards(state, this.opponent?.state ?? null);
    this.patch({ resyncs: this.view.resyncs + 1 });
  }

  /**
   * The seat is ours again. Take the server's copy of the board, flat.
   *
   * Nothing is reconciled: the client's own buffered history is not
   * re-applied on top of the snapshot. The match stopped when this seat went
   * away, so there was no match to have been playing. What this rewinds is
   * the stretch between the socket dying and the heartbeat noticing (at most
   * `HEARTBEAT_TIMEOUT_MS`), which is play that happened against a board
   * nobody else was moving.
   *
   * It does **not** start the loop. A `resumeAt` does, once every seat is back.
   */
  private applyResume(message: ResumeMessage): void {
    if (this.view.seat === null || message.seat !== this.view.seat) return;
    const mySeat = this.view.seat;
    const theirSeat: Seat = mySeat === 0 ? 1 : 0;

    const state = decodeSnapshot(message.snapshot);
    // A resume can be the first thing a fresh socket hears after a server
    // restart, so it carries the rules rather than trusting this client to
    // still know them — see `ResumeMessage.rules`.
    const rules = message.rules;
    this.coop = duelRuleSet(rules).coop;

    if (this.coop) {
      // One well, and the snapshot is it. Co-op always worked this way — the
      // partner went on playing on the one board there is — and since owner
      // overhaul part 9 the duel branch below works the same way for the same
      // reason, so the only thing left that is co-op-specific here is which
      // driver the board goes into.
      this.confirmed = this.driverFromSnapshot(state, rules, message.scheduled);
      this.confirmed.takeCheckpoints();
      this.opponent = null;
      this.pending = [];
      this.history = [];
      this.frame = state.tick;
      this.epoch = message.epoch;
      this.reportedTopOut = false;
      this.live = false;
      this.stopReconnecting();
      this.rebuildPrediction();
      this.room?.send(C2S.input, { frame: this.frame, events: [], epoch: this.epoch });
      this.patch({
        status: 'connected',
        statusDetail: '',
        resumes: this.view.resumes + 1,
        round: message.round,
        rules,
        handicap: message.handicap,
        phase: message.phase,
        graceMsLeft: null,
        reconnectAttempt: 0,
        presence: 'online',
      });
      return;
    }

    const driver = this.driverFromSnapshot(
      state,
      rules,
      message.scheduled,
      message.handicap[mySeat],
      message.curses,
    );

    this.own = driver;
    this.frame = driver.tick;
    // Nothing survives. Anything this client played after its socket died was
    // played while the match was stopped for everyone else, and the epoch
    // fences off whatever of it is still in flight the other way.
    this.history = [];
    this.pending = [];
    this.epoch = message.epoch;
    this.reportedTopOut = false;

    // A mirror cannot be replayed from relays that were never received, so the
    // opponent's board arrives as the server's own copy of it.
    const opponentState = decodeSnapshot(message.opponent.snapshot);
    this.opponent = this.driverFromSnapshot(
      opponentState,
      rules,
      message.opponent.scheduled,
      message.handicap[theirSeat],
      message.opponent.curses,
    );

    // Not `message.phase === 'playing'`: a resume arrives while the match is
    // still stopped, and `resumeAt` is what starts it.
    this.live = false;
    this.stopReconnecting();
    this.callbacks.onBoards(state, opponentState);

    // One empty batch, saying where this client now is. There is no buffered
    // history to deliver any more — which is also why there is no rate-limit
    // credit for delivering it (`docs/decisions/0041`).
    this.room?.send(C2S.input, { frame: this.frame, events: [], epoch: this.epoch });

    this.patch({
      status: 'connected',
      statusDetail: '',
      resumes: this.view.resumes + 1,
      round: message.round,
      rules,
      handicap: message.handicap,
      phase: message.phase,
      graceMsLeft: null,
      reconnectAttempt: 0,
      presence: 'online',
    });
  }

  /**
   * Everybody is back: count the 3-2-1 down and start the loop at zero.
   *
   * Locally, from `inMs`, rather than waiting for the `room` broadcast that
   * says the same thing a round trip later — every peer in the room got this
   * message at once and each runs its own copy of the same three seconds, which
   * is the closest to "together" a protocol with no shared clock can get. It is
   * exactly what `RoundStartMessage.startsInMs` already does before a round's
   * first tick.
   */
  private applyResumeAt(message: ResumeAtMessage): void {
    this.stopResumeCountdown();
    this.stopped = true;
    const inMs = Math.max(0, message.inMs);
    this.resumeEndsAt = Date.now() + inMs;
    this.patch({
      phase: 'resuming',
      round: message.round,
      matchStopped: true,
      resumeInMs: inMs,
      resumeAtTick: message.atTick,
    });

    this.resumeTickTimer = setInterval(() => {
      this.patch({ resumeInMs: Math.max(0, this.resumeEndsAt - Date.now()) });
    }, 100);

    this.resumeTimer = setTimeout(() => {
      this.stopResumeCountdown();
      // Only a resume *into play* releases the board here. Resuming into a
      // round's own countdown means there is still a countdown to sit through,
      // and the `room` that announces its end is what releases it — starting
      // now would put this client a whole countdown ahead of the authority,
      // which is the thing `beginRound` refuses to do for the same reason.
      if (message.phase === 'playing') {
        this.stopped = false;
        this.live = this.own !== null;
      }
      this.patch({
        phase: message.phase,
        matchStopped: this.stopped,
        resumeInMs: null,
        resumeAtTick: null,
      });
    }, inMs);
  }

  private stopResumeCountdown(): void {
    if (this.resumeTickTimer !== null) clearInterval(this.resumeTickTimer);
    if (this.resumeTimer !== null) clearTimeout(this.resumeTimer);
    this.resumeTickTimer = null;
    this.resumeTimer = null;
  }

  /* ---------------------------------------------------------------- *
   * Presence, the heartbeat and the reconnect ladder
   * ---------------------------------------------------------------- */

  private applyRoom(message: RoomMessage): void {
    const mine = this.view.seat === null ? undefined : message.players[this.view.seat];
    const theirs = message.players.find((player) => player.seat !== this.view.seat);
    const stopped = isStoppedPhase(message.phase);
    // The local 3-2-1 owns the phase while it is running: a `room` broadcast
    // saying `playing` may arrive a hair before or after this client's own
    // countdown reaches zero, and letting it win would either start the board
    // early or leave the overlay up after everybody else has started.
    const countingIn = this.resumeTimer !== null;
    // A stopped match is stopped for *everyone*, including the player who never
    // went anywhere. Done before the patch so a view handler that reads
    // `isPlaying` sees the truth. It is only *cleared* by a server that says
    // the match is running while nothing local disagrees: a countdown still
    // ticking here, or a socket this client knows is gone, both outrank it.
    if (stopped) this.stopped = true;
    else if (!countingIn && this.view.status !== 'reconnecting') this.stopped = false;
    this.patch({
      room: message,
      ...(countingIn ? {} : { phase: message.phase, matchStopped: this.stopped }),
      round: message.round,
      matchType: message.matchType,
      rules: message.rules,
      // While the socket is up the server's countdown is the authority; the
      // local one only exists for the stretch when nothing is arriving.
      ...(this.view.status === 'reconnecting'
        ? {}
        : { graceMsLeft: mine?.graceMsLeft ?? null, presence: mine?.presence ?? 'online' }),
      pauseMsLeft: mine?.pauseMsLeft ?? null,
      opponentPresence: theirs?.presence ?? 'online',
      opponentGraceMsLeft: theirs?.graceMsLeft ?? null,
      opponentPauseMsLeft: theirs?.pauseMsLeft ?? null,
    });
  }

  /**
   * Ask for a `pong` on a fixed interval, and give up on the socket when the
   * answers stop.
   *
   * This is the only detector for a socket that dies without a close frame,
   * which is exactly what a WiFi-to-mobile handoff produces. Without it the
   * client would keep sending into a dead socket for as long as the player kept
   * playing, and would discover the truth only when the grace window had
   * already expired.
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    if (this.callbacks.reconnect === undefined) return;
    this.lastPongMs = Date.now();
    this.heartbeatTimer = setInterval(() => {
      const now = Date.now();
      if (now - this.lastPongMs >= HEARTBEAT_TIMEOUT_MS) {
        this.onHeartbeatTimeout();
        return;
      }
      this.room?.send(C2S.ping, { t: now });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private onHeartbeatTimeout(): void {
    this.stopHeartbeat();
    const room = this.room;
    this.room = null;
    // Close it *unconsented*, so the server holds the seat rather than reading
    // a silent death as a departure. The socket is almost certainly already
    // dead; this is for the case where only one direction of it is.
    if (room !== null) void room.leave(false).catch(() => undefined);
    this.beginReconnecting('The connection stopped responding.');
  }

  private onSocketClosed(): void {
    this.stopHeartbeat();
    this.room = null;
    if (this.leaving || this.view.status === 'refused') {
      this.live = false;
      this.stopTimers();
      if (!this.leaving) this.emit();
      return;
    }
    if (this.callbacks.reconnect === undefined) {
      this.live = false;
      this.stopTimers();
      this.patch({ status: 'closed', statusDetail: 'Disconnected from the room.' });
      return;
    }
    this.beginReconnecting('Connection lost.');
  }

  private beginReconnecting(detail: string): void {
    if (this.view.status === 'reconnecting') return;
    this.attempt = 0;
    this.graceEndsAt = Date.now() + graceWindowMs(this.view.matchType);
    // Stop the board here rather than waiting to be told. `docs/DESIGN.md`,
    // 2026-08-24: "The disconnected player's client also stops simulating on
    // its side (it knows the match pauses)." It does know — the server pauses
    // the moment it notices this socket is gone, and no `room` broadcast is
    // going to arrive over a socket that is gone to say so.
    //
    // The one stretch this cannot cover is a socket that died *silently*: the
    // heartbeat is what turns that into a call to this method, so a handoff
    // buys up to `HEARTBEAT_TIMEOUT_MS` of play that the resume then rewinds.
    this.stopped = true;
    this.stopResumeCountdown();
    this.patch({
      status: 'reconnecting',
      statusDetail: detail,
      graceMsLeft: graceWindowMs(this.view.matchType),
      presence: 'reconnecting',
      reconnectAttempt: 0,
      matchStopped: true,
      resumeInMs: null,
      resumeAtTick: null,
    });
    this.startGraceCountdown();
    this.scheduleReconnect();
  }

  /**
   * Count the seat hold down locally.
   *
   * While the socket is gone the server's broadcasts are not arriving, so the
   * only countdown there can be is this one. It is an estimate — the server
   * started its window when *it* noticed — and it is deliberately the
   * pessimistic one, because a countdown that runs out early and then gets
   * corrected by a successful rejoin is better than one that promises time the
   * seat no longer has.
   */
  private startGraceCountdown(): void {
    if (this.graceTimer !== null) clearInterval(this.graceTimer);
    this.graceTimer = setInterval(() => {
      const left = Math.max(0, this.graceEndsAt - Date.now());
      this.patch({ graceMsLeft: left });
      if (left > 0) return;
      this.onGraceExhausted();
    }, PRESENCE_BROADCAST_MS);
  }

  /**
   * The window this client can see is spent. Stop climbing the ladder.
   *
   * What happened at the other end depends on the match type, and this says so
   * rather than guessing: a friendly match is frozen and can still be resumed
   * on demand, a ranked one has been forfeited.
   */
  private onGraceExhausted(): void {
    this.stopReconnecting();
    this.live = false;
    this.stopTimers();
    this.patch({
      status: 'closed',
      graceMsLeft: 0,
      statusDetail:
        this.view.matchType === 'ranked'
          ? 'The reconnect window expired and the match was forfeited.'
          : 'The reconnect window expired. The match is being held — try rejoining.',
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    const delay = reconnectDelayMs(this.attempt);
    this.patch({ reconnectAttempt: this.attempt + 1 });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.tryReconnect();
    }, delay);
  }

  /**
   * One rung of the ladder. Public, because the screen offers a "try again"
   * button once the window has run out and a frozen friendly match is still
   * there to be resumed.
   */
  tryReconnect(): void {
    const reconnect = this.callbacks.reconnect;
    if (reconnect === undefined || this.leaving || this.room !== null) return;
    if (this.reconnectInFlight) return;
    this.reconnectInFlight = true;
    reconnect().then(
      (room) => {
        this.reconnectInFlight = false;
        if (this.leaving) {
          void room.leave(true).catch(() => undefined);
          return;
        }
        // `attach` puts the status back to `connected`; the `resume` that
        // follows is what puts the board back.
        this.attach(room, this.view.role);
      },
      () => {
        this.reconnectInFlight = false;
        this.attempt += 1;
        if (this.view.status !== 'reconnecting') return;
        this.scheduleReconnect();
      },
    );
  }

  private stopReconnecting(): void {
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    if (this.graceTimer !== null) clearInterval(this.graceTimer);
    this.reconnectTimer = null;
    this.graceTimer = null;
    this.attempt = 0;
  }

  private stopTimers(): void {
    if (this.startTimer !== null) clearTimeout(this.startTimer);
    if (this.countdownTimer !== null) clearInterval(this.countdownTimer);
    this.startTimer = null;
    this.countdownTimer = null;
    // A round starting supersedes any resume countdown still running: the
    // boards are about to be rebuilt, so there is nothing left to resume into.
    this.stopResumeCountdown();
  }

  private patch(changes: Partial<DuelView>): void {
    this.view = { ...this.view, ...changes };
    this.emit();
  }

  private emit(): void {
    this.callbacks.onView({ ...this.view });
  }
}

function pieceAt(state: GameState | null, slot: number): ActivePiece | null {
  return state?.pieces[slot] ?? null;
}

/**
 * Did reconciliation move this piece further than the ordinary one cell?
 *
 * A piece that appeared or vanished counts, and so does a rotation or a
 * different letter: all three are the board saying something the player did not
 * ask for. The threshold itself is `docs/prompts/06-coop.md`'s — "never
 * teleports a piece more than one cell without a visible correction cue" — and
 * one cell is left silent on purpose, because a prediction that is off by a
 * single row of gravity is the system working.
 */
function teleported(before: ActivePiece | null, after: ActivePiece | null): boolean {
  if (before === null || after === null) return before !== after;
  if (before.kind !== after.kind || before.rotation !== after.rotation) return true;
  return Math.abs(before.x - after.x) + Math.abs(before.y - after.y) > 1;
}
